import type { ReactNode } from 'react';
import { Button } from './Button';

export interface ModalProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  width?: number;
  open?: boolean;
}

function ModalBase({ title, subtitle, children, footer, onClose, width = 480, open = true }: ModalProps) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        fontFamily: 'var(--font-body)',
      }}
    >
      <div
        style={{
          width,
          maxWidth: 'calc(100vw - 48px)',
          background: 'var(--surface-1)',
          border: '1px solid var(--line-strong)',
          borderRadius: 'var(--r-xl)',
          boxShadow: 'var(--shadow-modal)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          color: 'var(--text-1)',
          animation: 'tv-rise .25s cubic-bezier(.22,.61,.36,1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: '-.02em' }}>{title}</span>
            {subtitle && <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{subtitle}</span>}
          </div>
          {onClose && (
            <span
              onClick={onClose}
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 8,
                border: '1px solid var(--line-strong)',
                color: 'var(--text-3)',
                cursor: 'pointer',
              }}
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
              </svg>
            </span>
          )}
        </div>
        {children}
        {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{footer}</div>}
      </div>
    </div>
  );
}

/** `Modal` with the convenience `Modal.Button` slot preserved from the handoff. */
export const Modal = Object.assign(ModalBase, { Button });
